import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { isPlayDifficultyLockedApiError, isQuizLockedApiError } from '../utils/quizLockedApiError';
import { Container, Row, Col, Card, Form, Button, Badge, Pagination, Alert, Spinner, Modal, Collapse } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { Quiz, quizService, QuizFilters, SOLO_QUICK_MATCH_ROUTE_ID, inventoryService, pvpService, type User } from '../services/api';
import { shouldPromptXpPotionReminder } from '../utils/inventoryXpPotions';
import {
  inventorySlotsHaveUnusedHealthPotion,
  shouldPromptHealthPotionReminder,
} from '../utils/inventoryHealthPotions';
import XpPotionReminderModal from './XpPotionReminderModal';
import HealthPotionReminderModal from './HealthPotionReminderModal';
import LivesGateModal from './LivesGateModal';
import { isInsufficientLivesMessage, translateLivesOrRaw } from '../utils/livesErrorI18n';
import LoginPage from './LoginPage';
import './QuizList.css';
import { InlineHalfHeartIcon } from './LivesDisplay';
import { getQuizFullDisplayCaption } from '../utils/quizDisplayTitle';
import QuizTwoLineHeading from './QuizTwoLineHeading';
import { isQuizCareerLocked } from '../utils/quizCareerLock';
import { isUnlimitedUserType } from './LivesDisplay';
import QuizLockedModal from './QuizLockedModal';
import PvpCharacterUnlockRosterModal from './PvpCharacterUnlockRosterModal';
import { MOBILE_QUIZ_CARD_COLS_STORAGE_KEY } from '../constants/mobileQuizCardLayoutStorage';
import {
  SOLO_PVP_LOBBY_DEFAULT_PAGE_SIZE,
  SOLO_PVP_LOBBY_PAGE_SIZE_OPTIONS,
} from '../constants/soloPvpLobbyPagination';
import { careerHomePathWithSearch } from '../constants/careerSearchParams';
import { useMatchMedia } from '../hooks/useMatchMedia';
import { isQuizImageUrlLegacyHttp, quizPreviewImageUrlsFromKey } from '../utils/quizPreviewImageSrc';
import { resolveQuizImageAssetsFolder } from '../utils/quizQuestionImageSrc';
import { defaultHighestUnlockedPlayDifficulty } from '../utils/quizPlayModes';

/** Solo card: ícone de info / modal de silhuetas — cadeia de desbloqueio e/ou personagens só Médio-Difícil. */
function quizHasCharacterUnlockLobbyInfo(quiz: Quiz): boolean {
  return (
    quiz.characterUnlockEnabled === true || quiz.characterUnlockLobbyPreview === true
  );
}

/** Solo modal: enabled play bands from quiz authoring flags (fallback single band from catalog difficulty). */
function soloPlayModeChoices(quiz: Quiz): string[] {
  const out: string[] = [];
  if (quiz.supportsPlayDifficultyEasy) out.push('Easy');
  if (quiz.supportsPlayDifficultyMedium) out.push('Medium');
  if (quiz.supportsPlayDifficultyHard) out.push('Hard');
  if (out.length > 0) return out;
  const d = (quiz.difficulty || 'Easy').trim();
  if (d === 'Easy' || d === 'Medium' || d === 'Hard') return [d];
  return ['Easy'];
}

function isStopQuizFormat(quiz: Quiz): boolean {
  const f = (quiz.format || '').toString();
  return f === 'Stop' || f === 'stop';
}

function isGeniusQuizFormat(quiz: Quiz): boolean {
  const f = (quiz.format || '').toString();
  return f === 'Genius' || f === 'genius';
}

function isTileMatchQuizFormat(quiz: Quiz): boolean {
  const f = (quiz.format || '').toString();
  return f === 'TileMatch' || f === 'tilematch';
}

/** Career-gated solo bands (Medium/Hard) until missions unlock — Easy stays available when offered. */
function isSoloBandCareerLocked(quiz: Quiz, band: string): boolean {
  if (isStopQuizFormat(quiz) || isGeniusQuizFormat(quiz) || isTileMatchQuizFormat(quiz)) return false;
  if (band === 'Medium')
    return (
      quiz.supportsPlayDifficultyMedium === true &&
      quiz.playMediumUnlockedForUser === false
    );
  if (band === 'Hard')
    return quiz.supportsPlayDifficultyHard === true && quiz.playHardUnlockedForUser === false;
  return false;
}

function defaultSoloModalDifficulty(quiz: Quiz): string {
  return defaultHighestUnlockedPlayDifficulty(quiz);
}

/** Imagem do preview no canto superior direito do card (assets ou URL legada). */
const QuizListCardTopImage: React.FC<{ imageUrl: string; alt: string; decorative?: boolean }> = ({
  imageUrl,
  alt,
  decorative = false,
}) => {
  const legacy = isQuizImageUrlLegacyHttp(imageUrl);
  const urls = useMemo(
    () => (legacy ? null : quizPreviewImageUrlsFromKey(imageUrl)),
    [legacy, imageUrl]
  );
  const [ext, setExt] = useState<'webp' | 'png'>('webp');
  useEffect(() => {
    setExt('webp');
  }, [imageUrl]);
  if (legacy) {
    return (
      <img
        src={imageUrl.trim()}
        alt={decorative ? '' : alt}
        className="quiz-card-preview-img"
        decoding="async"
        aria-hidden={decorative ? true : undefined}
      />
    );
  }
  if (!urls) return null;
  const src = ext === 'webp' ? urls.webp : urls.png;
  return (
    <img
      key={`${imageUrl}-${ext}`}
      src={src}
      alt={decorative ? '' : alt}
      className="quiz-card-preview-img"
      decoding="async"
      aria-hidden={decorative ? true : undefined}
      onError={() => {
        if (ext === 'webp') setExt('png');
      }}
    />
  );
};

interface QuizListProps {
  isUserLoggedIn?: boolean;
  /** Used to block solo start client-side when out of lives (VIP upsell). */
  user?: User | null;
  onLoginSuccess?: () => void;
  /** Quando definido, a lista tenta destacar e rolar até o cartão deste quiz (ex. modo carreira). */
  focusQuizId?: string | null;
  onFocusQuizConsumed?: () => void;
  /** Vindo do modo carreira: abre o modal de confirmação para iniciar solo neste quiz (após consumo, limpar no pai). */
  autoOpenSoloConfirmQuizId?: string | null;
  onAutoOpenSoloConfirmConsumed?: () => void;
  /** Aba principal da home — ao voltar de Carreira para Solo, recarrega a lista (ex.: cadeado após missão). */
  mainTab?: 'solo' | 'pvp' | 'career';
}

type HealthReminderFollowUp =
  | { mode: 'solo'; quiz: Quiz }
  | { mode: 'quickMatch' };

const QuizList: React.FC<QuizListProps> = ({
  isUserLoggedIn = true,
  user = null,
  onLoginSuccess,
  focusQuizId = null,
  onFocusQuizConsumed,
  autoOpenSoloConfirmQuizId = null,
  onAutoOpenSoloConfirmConsumed,
  mainTab = 'solo',
}) => {
  const { t, i18n } = useSafeTranslation();
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  /** Boolean so we translate at render — `t()` during early fetch returns EN fallback before i18n is ready. */
  const [loadError, setLoadError] = useState(false);
  const [filters, setFilters] = useState<QuizFilters>(() => ({
    page: 1,
    pageSize: SOLO_PVP_LOBBY_DEFAULT_PAGE_SIZE,
    difficulty: '',
    search: '',
  }));
  const [pagination, setPagination] = useState({
    totalCount: 0,
    page: 1,
    pageSize: SOLO_PVP_LOBBY_DEFAULT_PAGE_SIZE,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [showLoginModal, setShowLoginModal] = useState(false);
  /** Mobile (below md): funnel button toggles the gray search/filter card */
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  /** Below md breakpoint: 1 or 2 cards per row (same as PvP lobby); persisted (shared storage key). */
  const [mobileCardCols, setMobileCardCols] = useState<1 | 2>(() => {
    if (typeof window === 'undefined') return 1;
    try {
      const v = localStorage.getItem(MOBILE_QUIZ_CARD_COLS_STORAGE_KEY);
      if (v === '2') return 2;
    } catch {
      /* ignore */
    }
    return 1;
  });

  useEffect(() => {
    try {
      localStorage.setItem(MOBILE_QUIZ_CARD_COLS_STORAGE_KEY, String(mobileCardCols));
    } catch {
      /* ignore */
    }
  }, [mobileCardCols]);
  /** Mobile grid 2/col: badges na mesma linha (cartão estreito). */
  const isNarrowViewport = useMatchMedia('(max-width: 767.98px)');
  const compactSoloGridHeader = isNarrowViewport && mobileCardCols === 2;
  const soloMobileOneCol = isNarrowViewport && mobileCardCols === 1;
  const [startingQuizId, setStartingQuizId] = useState<string | null>(null);
  const [livesGateOpen, setLivesGateOpen] = useState(false);
  const [livesGateMessage, setLivesGateMessage] = useState('');
  const [livesGateShowVipUpsell, setLivesGateShowVipUpsell] = useState(true);
  /** Confirmação antes de iniciar (mesmo padrão do modal PvP). */
  const [soloConfirmQuiz, setSoloConfirmQuiz] = useState<Quiz | null>(null);
  const [quickMatchConfirmOpen, setQuickMatchConfirmOpen] = useState(false);
  /** When the quiz has multiple play difficulties, POST /start sends this band. */
  const [soloConfirmPlayDifficulty, setSoloConfirmPlayDifficulty] = useState<string>('Easy');
  /** Quiz desbloqueável ainda bloqueado — modal de aviso. */
  const [quizLockedModalQuiz, setQuizLockedModalQuiz] = useState<Quiz | null>(null);
  /** Silhuetas / pré-requisitos de personagens (mesmo quiz). */
  const [unlockRosterModal, setUnlockRosterModal] = useState<{
    quizId: string;
    caption: string;
    imageAssetsBasePathFallback: string;
  } | null>(null);
  /** Medium/Hard bloqueado por missão — modal específico (sobre o modal de confirmar solo). */
  const [soloDifficultyLockedModalQuiz, setSoloDifficultyLockedModalQuiz] = useState<Quiz | null>(null);
  /** XP potion still in inventory — remind before opening solo confirm, quick match confirm, or PvP create (lobby). */
  const [xpPotionGateOpen, setXpPotionGateOpen] = useState(false);
  const [xpPotionPendingQuiz, setXpPotionPendingQuiz] = useState<Quiz | null>(null);
  /** If true, "continue without bonus" opens the quick match confirm modal instead of solo confirm. */
  const [xpPotionReminderQuickMatchNext, setXpPotionReminderQuickMatchNext] = useState(false);
  const [xpGateCheckingId, setXpGateCheckingId] = useState<string | null>(null);
  const [quickMatchGateChecking, setQuickMatchGateChecking] = useState(false);
  /** Health potion reminder (5 or fewer lives, not full) before solo / quick match confirm. */
  const [healthPotionGateOpen, setHealthPotionGateOpen] = useState(false);
  const [healthReminderFollowUp, setHealthReminderFollowUp] = useState<HealthReminderFollowUp | null>(
    null
  );
  const prevMainTab = useRef(mainTab);
  const autoOpenSoloConfirmHandledRef = useRef(false);

  const requestSoloConfirmModal = useCallback(
    async (quiz: Quiz): Promise<void> => {
      if (!isUserLoggedIn) {
        setShowLoginModal(true);
        return;
      }
      setXpGateCheckingId(quiz.id);
      try {
        const inv = await inventoryService.getMine();
        if (shouldPromptXpPotionReminder(user, inv.slots)) {
          setXpPotionReminderQuickMatchNext(false);
          setXpPotionPendingQuiz(quiz);
          setXpPotionGateOpen(true);
          return;
        }
      } catch {
        /* seguir sem gate se inventário falhar */
      } finally {
        setXpGateCheckingId(null);
      }
      try {
        const invHealth = await inventoryService.getMine();
        if (
          user &&
          shouldPromptHealthPotionReminder(user) &&
          inventorySlotsHaveUnusedHealthPotion(invHealth.slots)
        ) {
          setHealthReminderFollowUp({ mode: 'solo', quiz });
          setHealthPotionGateOpen(true);
          return;
        }
      } catch {
        /* seguir */
      }
      setSoloConfirmQuiz(quiz);
    },
    [isUserLoggedIn, user]
  );

  const requestQuickMatchModal = useCallback(async () => {
    if (!isUserLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    setQuickMatchGateChecking(true);
    try {
      const inv = await inventoryService.getMine();
      if (shouldPromptXpPotionReminder(user, inv.slots)) {
        setXpPotionPendingQuiz(null);
        setXpPotionReminderQuickMatchNext(true);
        setXpPotionGateOpen(true);
        return;
      }
      if (
        user &&
        shouldPromptHealthPotionReminder(user) &&
        inventorySlotsHaveUnusedHealthPotion(inv.slots)
      ) {
        setHealthReminderFollowUp({ mode: 'quickMatch' });
        setHealthPotionGateOpen(true);
        return;
      }
    } catch {
      /* seguir sem gate */
    } finally {
      setQuickMatchGateChecking(false);
    }
    setQuickMatchConfirmOpen(true);
  }, [isUserLoggedIn, user]);

  const loadQuizzes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await quizService.getActiveQuizzes(filters);
      setQuizzes(response.quizzes);
      setPagination({
        totalCount: response.totalCount,
        page: response.page,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        hasNextPage: response.hasNextPage,
        hasPreviousPage: response.hasPreviousPage,
      });
      setLoadError(false);
    } catch {
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void loadQuizzes();
  }, [loadQuizzes]);

  useEffect(() => {
    if (mainTab === 'solo' && prevMainTab.current === 'career') {
      void loadQuizzes();
    }
    prevMainTab.current = mainTab;
  }, [mainTab, loadQuizzes]);

  useEffect(() => {
    if (!focusQuizId) return;
    if (isLoading) return;

    const id = focusQuizId;

    if (!quizzes.some((q) => q.id === id)) {
      void quizService
        .getActiveQuiz(id)
        .then((q) => {
          setQuizzes((prev) => (prev.some((x) => x.id === q.id) ? prev : [q, ...prev]));
        })
        .catch(() => {
          onFocusQuizConsumed?.();
        });
      return;
    }

    let scrollTimer: number | undefined;
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-quiz-card-id="${id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('quiz-card--focus-highlight');
        scrollTimer = window.setTimeout(() => {
          el.classList.remove('quiz-card--focus-highlight');
          onFocusQuizConsumed?.();
        }, 2200);
      } else {
        onFocusQuizConsumed?.();
      }
    });
    return () => {
      cancelAnimationFrame(raf);
      if (scrollTimer !== undefined) window.clearTimeout(scrollTimer);
    };
  }, [focusQuizId, isLoading, quizzes, onFocusQuizConsumed]);

  useEffect(() => {
    if (!soloConfirmQuiz) return;
    setSoloConfirmPlayDifficulty(defaultSoloModalDifficulty(soloConfirmQuiz));
  }, [soloConfirmQuiz]);

  /** Vindo do modo carreira: abre o modal de confirmação (ou bloqueado) para o quiz de origem. */
  useEffect(() => {
    if (!autoOpenSoloConfirmQuizId) {
      autoOpenSoloConfirmHandledRef.current = false;
      return;
    }
    if (isLoading) return;
    if (autoOpenSoloConfirmHandledRef.current) return;

    const id = autoOpenSoloConfirmQuizId;

    const finish = () => {
      autoOpenSoloConfirmHandledRef.current = true;
      onAutoOpenSoloConfirmConsumed?.();
    };

    const runOpen = async (quiz: Quiz) => {
      if (isQuizCareerLocked(quiz)) {
        setQuizLockedModalQuiz(quiz);
        finish();
        return;
      }
      await requestSoloConfirmModal(quiz);
      finish();
    };

    const existing = quizzes.find((q) => q.id === id);
    if (existing) {
      void runOpen(existing);
      return;
    }

    let cancelled = false;
    quizService
      .getActiveQuiz(id)
      .then((q) => {
        if (cancelled) return;
        if (autoOpenSoloConfirmHandledRef.current) return;
        setQuizzes((prev) => (prev.some((x) => x.id === q.id) ? prev : [q, ...prev]));
        void runOpen(q);
      })
      .catch(() => {
        if (cancelled) return;
        if (autoOpenSoloConfirmHandledRef.current) return;
        finish();
      });

    return () => {
      cancelled = true;
    };
  }, [autoOpenSoloConfirmQuizId, isLoading, quizzes, onAutoOpenSoloConfirmConsumed, requestSoloConfirmModal]);

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setFilters(prev => ({ ...prev, pageSize, page: 1 }));
  };

  const handleDifficultyChange = (difficulty: string) => {
    setFilters(prev => ({ ...prev, difficulty, page: 1 }));
  };

  const handleSearchChange = (search: string) => {
    setFilters(prev => ({ ...prev, search, page: 1 }));
  };

  const formatQuizDifficulty = (difficulty: string) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy':
        return t('quiz.easy', 'Easy');
      case 'medium':
        return t('quiz.medium', 'Medium');
      case 'hard':
        return t('quiz.hard', 'Hard');
      default:
        return difficulty;
    }
  };

  const runQuickMatchAfterConfirm = async () => {
    setStartingQuizId('__quick_match__');
    setLivesGateOpen(false);
    try {
      const start = await quizService.startSoloQuickMatch();
      navigate(`/quiz/${SOLO_QUICK_MATCH_ROUTE_ID}`, { state: { soloQuizStart: start } });
      setQuickMatchConfirmOpen(false);
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setLivesGateShowVipUpsell(false);
        setLivesGateMessage(
          t('quiz.quickMatchNotEnoughQuizzes', 'Not enough quizzes available for a random match.')
        );
        setLivesGateOpen(true);
        return;
      }
      const raw =
        axios.isAxiosError(err) && err.response?.data && typeof (err.response.data as { message?: string }).message === 'string'
          ? (err.response.data as { message: string }).message
          : t('quiz.loadFailed', 'Failed to load quiz');
      setLivesGateShowVipUpsell(true);
      setLivesGateOpen(true);
      setLivesGateMessage(isInsufficientLivesMessage(raw) ? '' : translateLivesOrRaw(t, raw));
    } finally {
      setStartingQuizId(null);
    }
  };

  const runSoloQuizAfterConfirm = async (quiz: Quiz) => {
    if (isQuizCareerLocked(quiz)) {
      setQuizLockedModalQuiz(quiz);
      return;
    }
    if (isSoloBandCareerLocked(quiz, soloConfirmPlayDifficulty)) {
      setSoloDifficultyLockedModalQuiz(quiz);
      return;
    }
    setStartingQuizId(quiz.id);
    setLivesGateOpen(false);
    try {
      const playDifficultyForStart =
        isStopQuizFormat(quiz) || isGeniusQuizFormat(quiz) || isTileMatchQuizFormat(quiz)
          ? 'Medium'
          : soloConfirmPlayDifficulty;

      const { room } = await pvpService.joinOldestForQuiz({
        quizId: quiz.id,
        playDifficulty: playDifficultyForStart,
      });
      if (room) {
        setSoloConfirmQuiz(null);
        const path = (room.roomCode && room.roomCode.trim()) || room.id;
        navigate(`/pvp-quiz/${path}`, { state: { fromPvpLobby: true } });
        return;
      }

      const start = await quizService.startSoloQuiz(quiz.id, {
        playDifficulty: playDifficultyForStart,
      });
      navigate(`/quiz/${quiz.id}`, { state: { soloQuizStart: start } });
      setSoloConfirmQuiz(null);
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        const id = (err.response?.data as { activeRoomId?: string })?.activeRoomId;
        if (id) {
          setSoloConfirmQuiz(null);
          navigate(`/pvp-quiz/${id}`, { state: { fromPvpLobby: true } });
          return;
        }
      }
      if (isQuizLockedApiError(err)) {
        setQuizLockedModalQuiz(quiz);
        return;
      }
      if (isPlayDifficultyLockedApiError(err)) {
        setLivesGateShowVipUpsell(false);
        setLivesGateMessage(t('quiz.playDifficultyLockedBody'));
        setLivesGateOpen(true);
        return;
      }
      const raw =
        axios.isAxiosError(err) && err.response?.data && typeof (err.response.data as { message?: string }).message === 'string'
          ? (err.response.data as { message: string }).message
          : t('quiz.loadFailed', 'Failed to load quiz');
      setLivesGateShowVipUpsell(true);
      setLivesGateOpen(true);
      setLivesGateMessage(isInsufficientLivesMessage(raw) ? '' : translateLivesOrRaw(t, raw));
    } finally {
      setStartingQuizId(null);
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    if (onLoginSuccess) {
      onLoginSuccess();
    }
  };

  return (
    <Container fluid className="quiz-list-page">
      <Row className="mb-0">
        <Col xs={12} className="px-3">
          <div className="quiz-list-hero-stack quiz-list-hero-stack--mobile-filter-corner">
            <Button
              type="button"
              variant={mobileFiltersOpen ? 'secondary' : 'outline-secondary'}
              size="sm"
              className="quiz-list-mobile-filters-toggle-btn d-md-none rounded-3 shadow-sm"
              aria-pressed={mobileFiltersOpen}
              onClick={(e) => {
                setMobileFiltersOpen((o) => !o);
                /* Mobile: Bootstrap mantém focus após tap — estado “pressionado/cinza” persiste até blur */
                const el = e.currentTarget;
                window.setTimeout(() => {
                  el.blur();
                }, 0);
              }}
              aria-expanded={mobileFiltersOpen}
              aria-controls="quiz-list-mobile-filters-panel"
              title={t('quiz.toggleQuizFiltersAria', 'Search and filter quizzes')}
              aria-label={t('quiz.toggleQuizFiltersAria', 'Search and filter quizzes')}
            >
              <i className={`bi bi-funnel ${mobileFiltersOpen ? 'text-white' : ''}`} aria-hidden />
            </Button>
            <div className="quiz-list-header">
              <h1>{t('quiz.availableQuizzes', 'Available Quizzes')}</h1>
              <p className="text-muted">{t('quiz.chooseQuiz', 'Test your knowledge!')}</p>
            </div>
            <div className="quiz-list-hero-cta-row">
              <span className="quiz-list-quick-match-wrap" hidden>
                <Button
                  type="button"
                  variant="success"
                  size="lg"
                  className="quiz-list-quick-match-btn fw-semibold px-4 py-3 rounded-3 shadow-sm"
                  disabled={quickMatchGateChecking}
                  onClick={() => void requestQuickMatchModal()}
                  aria-label={t('quiz.quickMatchButton', 'Random match')}
                >
                  {quickMatchGateChecking ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                      {t('common.loading', 'Loading...')}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-shuffle me-2" aria-hidden />
                      {t('quiz.quickMatchButton', 'Random match')}
                    </>
                  )}
                </Button>
              </span>
            </div>
          </div>
        </Col>
      </Row>

      {/* Filters — mb só em md+; no mobile o painel fechado deixava faixa vazia acima de «Mostrando…» */}
      <Row className="mb-0 mb-md-4">
        <Col>
          <div className="d-md-none">
            <Collapse in={mobileFiltersOpen}>
              <div id="quiz-list-mobile-filters-panel">
                <Card>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('quiz.searchQuizzes', 'Search Quizzes')}</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={t('quiz.searchPlaceholder', 'Search by title or theme...')}
                        value={filters.search || ''}
                        onChange={(e) => handleSearchChange(e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('quiz.difficulty', 'Difficulty')}</Form.Label>
                      <Form.Select
                        value={filters.difficulty || ''}
                        onChange={(e) => handleDifficultyChange(e.target.value)}
                      >
                        <option value="">{t('quiz.allDifficulties', 'All Difficulties')}</option>
                        <option value="Easy">{t('quiz.easy', 'Easy')}</option>
                        <option value="Medium">{t('quiz.medium', 'Medium')}</option>
                        <option value="Hard">{t('quiz.hard', 'Hard')}</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('quiz.itemsPerPage', 'Items per page')}</Form.Label>
                      <Form.Select
                        value={filters.pageSize || SOLO_PVP_LOBBY_DEFAULT_PAGE_SIZE}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      >
                        {SOLO_PVP_LOBBY_PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Button
                      variant="outline-secondary"
                      className="w-100"
                      onClick={() => {
                        setFilters({
                          page: 1,
                          pageSize: SOLO_PVP_LOBBY_DEFAULT_PAGE_SIZE,
                          difficulty: '',
                          search: '',
                        });
                      }}
                    >
                      {t('quiz.clearFilters', 'Clear Filters')}
                    </Button>
                  </Card.Body>
                </Card>
              </div>
            </Collapse>
          </div>

          <div className="d-none d-md-block">
            <Card>
              <Card.Body>
              {/* Desktop (md+): original single-row layout */}
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t('quiz.searchQuizzes', 'Search Quizzes')}</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={t('quiz.searchPlaceholder', 'Search by title or theme...')}
                        value={filters.search || ''}
                        onChange={(e) => handleSearchChange(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>{t('quiz.difficulty', 'Difficulty')}</Form.Label>
                      <Form.Select
                        value={filters.difficulty || ''}
                        onChange={(e) => handleDifficultyChange(e.target.value)}
                      >
                        <option value="">{t('quiz.allDifficulties', 'All Difficulties')}</option>
                        <option value="Easy">{t('quiz.easy', 'Easy')}</option>
                        <option value="Medium">{t('quiz.medium', 'Medium')}</option>
                        <option value="Hard">{t('quiz.hard', 'Hard')}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>{t('quiz.itemsPerPage', 'Items per page')}</Form.Label>
                      <Form.Select
                        value={filters.pageSize || SOLO_PVP_LOBBY_DEFAULT_PAGE_SIZE}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      >
                        {SOLO_PVP_LOBBY_PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setFilters({
                          page: 1,
                          pageSize: SOLO_PVP_LOBBY_DEFAULT_PAGE_SIZE,
                          difficulty: '',
                          search: '',
                        });
                      }}
                      className="w-100"
                    >
                      {t('quiz.clearFilters', 'Clear Filters')}
                    </Button>
                  </Col>
                </Row>
            </Card.Body>
          </Card>
          </div>
        </Col>
      </Row>

      {loadError && (
        <Alert variant="danger" dismissible onClose={() => setLoadError(false)}>
          {t('quiz.loadQuizzesError', 'Failed to load quizzes.')}
        </Alert>
      )}

      {/* Results Info — mobile: só «Página» à direita na 2ª linha; «Mostrando» inteiro na 1ª; desktop inalterado */}
      <Row className="mb-3">
        <Col>
          <div className="d-md-none">
            <p className="text-muted mb-0 text-center w-100 mb-2">
              {t('quiz.showing', 'Showing')} {((pagination.page - 1) * pagination.pageSize) + 1}{' '}
              {t('quiz.to', 'to')} {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)}{' '}
              {t('quiz.of', 'of')} {pagination.totalCount} {t('quiz.quizzes', 'quizzes')}
            </p>
            {pagination.totalCount > 0 && (
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="btn-group btn-group-sm pvp-lobby-card-layout-toggle d-flex"
                  role="group"
                  aria-label={t('quiz.mobileGridLayoutAria', 'Layout of quiz cards on small screens')}
                >
                  <Button
                    type="button"
                    variant={mobileCardCols === 1 ? 'primary' : 'outline-secondary'}
                    className="px-2 py-1"
                    onClick={() => setMobileCardCols(1)}
                    aria-pressed={mobileCardCols === 1}
                    title={t('pvp.mobileGridOnePerRow', 'One card per row')}
                  >
                    <i className="bi bi-list-ul" aria-hidden />
                    <span className="visually-hidden">
                      {t('pvp.mobileGridOnePerRow', 'One card per row')}
                    </span>
                  </Button>
                  <Button
                    type="button"
                    variant={mobileCardCols === 2 ? 'primary' : 'outline-secondary'}
                    className="px-2 py-1"
                    onClick={() => setMobileCardCols(2)}
                    aria-pressed={mobileCardCols === 2}
                    title={t('pvp.mobileGridTwoPerRow', 'Two cards per row')}
                  >
                    <i className="bi bi-grid-3x2-gap" aria-hidden />
                    <span className="visually-hidden">
                      {t('pvp.mobileGridTwoPerRow', 'Two cards per row')}
                    </span>
                  </Button>
                </div>
                <p className="text-muted mb-0 text-nowrap">
                  {t('quiz.page', 'Page')} {pagination.page} {t('quiz.of', 'of')} {pagination.totalPages}
                </p>
              </div>
            )}
          </div>
          <div className="d-none d-md-flex justify-content-between align-items-center flex-wrap gap-2 w-100">
            <p className="text-muted mb-0 flex-grow-1 min-w-0">
              {t('quiz.showing', 'Showing')} {((pagination.page - 1) * pagination.pageSize) + 1}{' '}
              {t('quiz.to', 'to')} {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)}{' '}
              {t('quiz.of', 'of')} {pagination.totalCount} {t('quiz.quizzes', 'quizzes')}
            </p>
            <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-md-auto">
              {pagination.totalCount > 0 && (
                <p className="text-muted mb-0 text-nowrap">
                  {t('quiz.page', 'Page')} {pagination.page} {t('quiz.of', 'of')} {pagination.totalPages}
                </p>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Quizzes Grid */}
      {isLoading ? (
        <Row>
          <Col className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Col>
        </Row>
      ) : quizzes.length > 0 ? (
        <>
          <Row className="g-2 g-md-3">
            {quizzes.map((quiz) => (
              <Col
                key={quiz.id}
                xs={mobileCardCols === 2 ? 6 : 12}
                md={6}
                lg={4}
                className="mb-3 mb-md-4"
              >
                <Card
                  data-quiz-card-id={quiz.id}
                  className={`quiz-card position-relative h-100${
                    compactSoloGridHeader ? ' d-flex flex-column w-100 quiz-card--solo-mobile-2col' : ''
                  }${quiz.imageUrl ? ' quiz-card--has-preview' : ''}${
                    quiz.recommendedSolo ? ' quiz-card--recommended-solo' : ''
                  }${soloMobileOneCol ? ' quiz-card--solo-mobile-1col' : ''}`}
                >
                  {quiz.imageUrl ? (
                    <div className="quiz-card-preview-slot">
                      <div className="quiz-card-preview-slot-shape" aria-hidden />
                      <div className="quiz-card-preview-slot-img-stack">
                        <div className="quiz-card-preview-slot-img-wrap quiz-card-preview-slot-img-wrap--upper">
                          <QuizListCardTopImage
                            imageUrl={quiz.imageUrl}
                            alt={getQuizFullDisplayCaption(quiz, i18n.language)}
                          />
                        </div>
                        <div className="quiz-card-preview-slot-img-wrap quiz-card-preview-slot-img-wrap--lower" aria-hidden>
                          <QuizListCardTopImage
                            imageUrl={quiz.imageUrl}
                            alt=""
                            decorative
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <Card.Body
                    className={`d-flex flex-column${compactSoloGridHeader ? ' flex-grow-1 min-h-0' : ''}`}
                  >
                    {quiz.recommendedSolo && (
                      <div
                        className={
                          compactSoloGridHeader
                            ? 'mb-0 d-flex align-items-center gap-2 min-w-0 quiz-card-solo-diff-lang-row'
                            : 'mb-2 text-start d-flex flex-wrap align-items-center gap-2'
                        }
                      >
                        <Badge bg="warning" text="dark" className="quiz-recommended-solo-badge">
                          {t('quiz.recommendedSolo', 'Recommended')}
                        </Badge>
                      </div>
                    )}
                    <div
                      className={`${
                        compactSoloGridHeader ? 'mb-0' : 'mb-2'
                      } text-center ${
                        compactSoloGridHeader ? 'px-0' : 'px-1'
                      } quiz-card-solo-title-only${
                        isQuizCareerLocked(quiz) && !compactSoloGridHeader
                          ? ' quiz-card-solo-title-only--career-locked'
                          : ''
                      }`}
                    >
                      {isQuizCareerLocked(quiz) && !compactSoloGridHeader ? (
                        <span className="quiz-card-solo-lock-row">
                          <i
                            className="bi bi-lock-fill text-secondary quiz-card-solo-lock-icon"
                            title={i18n.t('quiz.unlockableListHint', {
                              defaultValue: 'Unlockable quiz — shown with lock in lobbies',
                            })}
                            aria-label={i18n.t('quiz.unlockableListHint', {
                              defaultValue: 'Unlockable quiz — shown with lock in lobbies',
                            })}
                          />
                        </span>
                      ) : null}
                      <Card.Title
                        as="h5"
                        className="quiz-title quiz-card-solo-title-heading mb-0 text-center w-100"
                      >
                        <QuizTwoLineHeading quiz={quiz} language={i18n.language} />
                      </Card.Title>
                    </div>
                    {compactSoloGridHeader &&
                    (isQuizCareerLocked(quiz) || quizHasCharacterUnlockLobbyInfo(quiz)) ? (
                      <div className="quiz-card-solo-middle flex-grow-1 d-flex flex-column align-items-center justify-content-center min-h-0 gap-1">
                        {isQuizCareerLocked(quiz) ? (
                          <span className="quiz-card-solo-lock-row quiz-card-solo-lock-row--in-flow">
                            <i
                              className="bi bi-lock-fill text-secondary quiz-card-solo-lock-icon"
                              title={i18n.t('quiz.unlockableListHint', {
                                defaultValue: 'Unlockable quiz — shown with lock in lobbies',
                              })}
                              aria-label={i18n.t('quiz.unlockableListHint', {
                                defaultValue: 'Unlockable quiz — shown with lock in lobbies',
                              })}
                            />
                          </span>
                        ) : null}
                        {quizHasCharacterUnlockLobbyInfo(quiz) ? (
                          <Button
                            type="button"
                            variant="light"
                            size="sm"
                            className="quiz-card-character-unlock-info-btn quiz-card-character-unlock-info-btn--solo-2col-inline rounded-circle border shadow-sm"
                            aria-label={t(
                              'quiz.characterUnlockInfoAria',
                              'Unlockable characters in this quiz'
                            )}
                            title={t(
                              'quiz.characterUnlockInfoAria',
                              'Unlockable characters in this quiz'
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setUnlockRosterModal({
                                quizId: quiz.id,
                                caption: getQuizFullDisplayCaption(quiz, i18n.language),
                                imageAssetsBasePathFallback: resolveQuizImageAssetsFolder(quiz),
                              });
                            }}
                          >
                            <i className="bi bi-info-circle" aria-hidden />
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                    <div
                      className={`quiz-card-solo-actions w-100 flex-shrink-0 ${
                        compactSoloGridHeader
                          ? `d-flex flex-column align-items-center ${
                              isQuizCareerLocked(quiz) || quizHasCharacterUnlockLobbyInfo(quiz)
                                ? 'pt-0'
                                : 'mt-auto pt-0'
                            }`
                          : quizHasCharacterUnlockLobbyInfo(quiz)
                            ? 'd-flex flex-row flex-nowrap align-items-center quiz-card-solo-actions--start-row-with-info mt-auto pt-2'
                            : 'd-flex flex-column align-items-center mt-auto pt-2'
                      }`}
                    >
                      {!compactSoloGridHeader && quizHasCharacterUnlockLobbyInfo(quiz) ? (
                        <>
                          <Button
                            type="button"
                            variant="light"
                            size="sm"
                            className="quiz-card-character-unlock-info-btn quiz-card-character-unlock-info-btn--actions-row rounded-circle border shadow-sm"
                            aria-label={t(
                              'quiz.characterUnlockInfoAria',
                              'Unlockable characters in this quiz'
                            )}
                            title={t(
                              'quiz.characterUnlockInfoAria',
                              'Unlockable characters in this quiz'
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setUnlockRosterModal({
                                quizId: quiz.id,
                                caption: getQuizFullDisplayCaption(quiz, i18n.language),
                                imageAssetsBasePathFallback: resolveQuizImageAssetsFolder(quiz),
                              });
                            }}
                          >
                            <i className="bi bi-info-circle" aria-hidden />
                          </Button>
                          <div className="quiz-card-solo-actions-start-wrap flex-grow-1 d-flex justify-content-center align-items-center min-w-0 px-1">
                            <Button
                              variant="primary"
                              className="quiz-card-start-btn"
                              disabled={startingQuizId !== null || xpGateCheckingId === quiz.id}
                              onClick={() => {
                                if (isQuizCareerLocked(quiz)) {
                                  setQuizLockedModalQuiz(quiz);
                                  return;
                                }
                                void requestSoloConfirmModal(quiz);
                              }}
                            >
                              {startingQuizId === quiz.id ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  {t('quiz.loadingQuiz', 'Loading quiz...')}
                                </>
                              ) : xpGateCheckingId === quiz.id ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  {t('common.loading', 'Loading...')}
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-play-circle me-2"></i>
                                  {t('quiz.play', 'Play')}
                                </>
                              )}
                            </Button>
                          </div>
                          <span
                            className="quiz-card-solo-actions-sym-spacer flex-shrink-0"
                            aria-hidden
                          />
                        </>
                      ) : (
                        <Button
                          variant="primary"
                          className="quiz-card-start-btn"
                          disabled={startingQuizId !== null || xpGateCheckingId === quiz.id}
                          onClick={() => {
                            if (isQuizCareerLocked(quiz)) {
                              setQuizLockedModalQuiz(quiz);
                              return;
                            }
                            void requestSoloConfirmModal(quiz);
                          }}
                        >
                          {startingQuizId === quiz.id ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              {t('quiz.loadingQuiz', 'Loading quiz...')}
                            </>
                          ) : xpGateCheckingId === quiz.id ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              {t('common.loading', 'Loading...')}
                            </>
                          ) : (
                            <>
                              <i className="bi bi-play-circle me-2"></i>
                              {t('quiz.play', 'Play')}
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Row className="mt-4">
              <Col className="d-flex justify-content-center">
                <Pagination>
                  <Pagination.First 
                    disabled={!pagination.hasPreviousPage}
                    onClick={() => handlePageChange(1)}
                  />
                  <Pagination.Prev 
                    disabled={!pagination.hasPreviousPage}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  />
                  
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(pagination.totalPages - 4, pagination.page - 2)) + i;
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === pagination.page}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}
                  
                  <Pagination.Next 
                    disabled={!pagination.hasNextPage}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  />
                  <Pagination.Last 
                    disabled={!pagination.hasNextPage}
                    onClick={() => handlePageChange(pagination.totalPages)}
                  />
                </Pagination>
              </Col>
            </Row>
          )}
        </>
      ) : (
        <Row>
          <Col className="text-center py-5">
            <div className="empty-state">
              <i className="bi bi-question-circle text-muted" style={{ fontSize: '4rem' }}></i>
                             <h4 className="mt-3">{t('quiz.noQuizzesFound', 'No quizzes found')}</h4>
                <p className="text-muted">
                  {filters.search || filters.difficulty 
                   ? t('quiz.adjustSearchCriteria', 'Try adjusting your search criteria or filters.')
                   : t('quiz.noActiveQuizzes', 'There are no active quizzes available at the moment.')
                  }
              </p>
            </div>
          </Col>
        </Row>
      )}

      <XpPotionReminderModal
        show={xpPotionGateOpen}
        onHide={() => {
          setXpPotionGateOpen(false);
          setXpPotionPendingQuiz(null);
          setXpPotionReminderQuickMatchNext(false);
        }}
        onGoToInventory={() => {
          setXpPotionGateOpen(false);
          setXpPotionPendingQuiz(null);
          setXpPotionReminderQuickMatchNext(false);
          navigate('/inventory');
        }}
        onContinueWithoutBonus={() => {
          const q = xpPotionPendingQuiz;
          const openQuick = xpPotionReminderQuickMatchNext;
          setXpPotionGateOpen(false);
          setXpPotionPendingQuiz(null);
          setXpPotionReminderQuickMatchNext(false);
          void (async () => {
            try {
              const inv = await inventoryService.getMine();
              if (
                user &&
                shouldPromptHealthPotionReminder(user) &&
                inventorySlotsHaveUnusedHealthPotion(inv.slots)
              ) {
                if (openQuick) {
                  setHealthReminderFollowUp({ mode: 'quickMatch' });
                } else if (q) {
                  setHealthReminderFollowUp({ mode: 'solo', quiz: q });
                }
                setHealthPotionGateOpen(true);
                return;
              }
            } catch {
              /* seguir */
            }
            if (openQuick) {
              setQuickMatchConfirmOpen(true);
            } else if (q) {
              setSoloConfirmQuiz(q);
            }
          })();
        }}
      />

      <HealthPotionReminderModal
        show={healthPotionGateOpen}
        onHide={() => {
          setHealthPotionGateOpen(false);
          setHealthReminderFollowUp(null);
        }}
        onGoToInventory={() => {
          setHealthPotionGateOpen(false);
          setHealthReminderFollowUp(null);
          navigate('/inventory');
        }}
        onContinueWithoutUsing={() => {
          const f = healthReminderFollowUp;
          setHealthPotionGateOpen(false);
          setHealthReminderFollowUp(null);
          if (!f) return;
          if (f.mode === 'solo') {
            setSoloConfirmQuiz(f.quiz);
          } else {
            setQuickMatchConfirmOpen(true);
          }
        }}
      />

      <LivesGateModal
        show={livesGateOpen}
        message={livesGateMessage || undefined}
        showVipUpsell={livesGateShowVipUpsell}
        onHide={() => {
          setLivesGateOpen(false);
          setLivesGateShowVipUpsell(true);
        }}
      />

      <QuizLockedModal
        show={!!quizLockedModalQuiz}
        onHide={() => setQuizLockedModalQuiz(null)}
        quizTitle={
          quizLockedModalQuiz
            ? getQuizFullDisplayCaption(quizLockedModalQuiz, i18n.language)
            : ''
        }
        onGoCareer={() => {
          const caption = quizLockedModalQuiz
            ? getQuizFullDisplayCaption(quizLockedModalQuiz, i18n.language)
            : '';
          setQuizLockedModalQuiz(null);
          navigate(careerHomePathWithSearch(caption));
        }}
      />

      <QuizLockedModal
        variant="difficulty"
        show={!!soloDifficultyLockedModalQuiz}
        onHide={() => setSoloDifficultyLockedModalQuiz(null)}
        quizTitle={
          soloDifficultyLockedModalQuiz
            ? getQuizFullDisplayCaption(soloDifficultyLockedModalQuiz, i18n.language)
            : ''
        }
        onGoCareer={() => {
          const q = soloDifficultyLockedModalQuiz;
          const caption = q ? getQuizFullDisplayCaption(q, i18n.language) : '';
          setSoloDifficultyLockedModalQuiz(null);
          setSoloConfirmQuiz(null);
          navigate(careerHomePathWithSearch(caption));
        }}
      />

      <Modal
        show={quickMatchConfirmOpen}
        onHide={() => setQuickMatchConfirmOpen(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('quiz.soloJoinTitle', 'Play this mode?')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            <strong>{t('quiz.gameLabel', 'Game:')}</strong>{' '}
            {t('quiz.quickMatchQuizLabel', 'Aleatório')}
          </div>
          <div className="mb-2">
            <strong>{t('quiz.difficulty', 'Difficulty')}:</strong>{' '}
            {t('quiz.quickMatchDifficultyLabel', 'Aleatório')}
          </div>
          <div className="mb-0">
            <strong>{t('pvp.inviteJoinCost', 'Cost')}:</strong>{' '}
            <span className="d-inline-flex align-items-center gap-1 flex-wrap">
              <InlineHalfHeartIcon className="pvp-invite-join-cost-heart" />
              <span>× 1</span>
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-between flex-wrap gap-2">
          <Button variant="outline-secondary" onClick={() => setQuickMatchConfirmOpen(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          {!isUserLoggedIn ? (
            <Button variant="primary" onClick={() => setShowLoginModal(true)}>
              {t('quiz.soloJoinLogin', 'Log in to play')}
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={startingQuizId !== null}
              onClick={() => {
                if (
                  user &&
                  !isUnlimitedUserType(user.userType) &&
                  (user.lives ?? 0) < 1
                ) {
                  setQuickMatchConfirmOpen(false);
                  setLivesGateMessage('');
                  setLivesGateOpen(true);
                  return;
                }
                void runQuickMatchAfterConfirm();
              }}
            >
              {startingQuizId !== null ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t('quiz.loadingQuiz', 'Loading quiz...')}
                </>
              ) : (
                t('quiz.play', 'Play')
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!soloConfirmQuiz}
        onHide={() => setSoloConfirmQuiz(null)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('quiz.soloJoinTitle', 'Play this mode?')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {soloConfirmQuiz && (
            <>
              <div className="mb-2">
                <strong>{t('quiz.gameLabel', 'Game:')}</strong>{' '}
                {getQuizFullDisplayCaption(soloConfirmQuiz, i18n.language)}
              </div>
              <div className="mb-2">
                <strong>{t('quiz.difficulty', 'Difficulty')}:</strong>{' '}
                {soloPlayModeChoices(soloConfirmQuiz).length > 1 ? (
                  <Form.Select
                    size="sm"
                    className="d-inline-block w-auto align-middle ms-1"
                    value={soloConfirmPlayDifficulty}
                    onChange={(e) => setSoloConfirmPlayDifficulty(e.target.value)}
                    aria-label={t('quiz.difficulty', 'Difficulty')}
                  >
                    {soloPlayModeChoices(soloConfirmQuiz).map((band) => {
                      const locked = isSoloBandCareerLocked(soloConfirmQuiz, band);
                      const lockPrefix = locked
                        ? i18n.t('quiz.unlockableListPrefix', { defaultValue: '🔒 ' })
                        : '';
                      return (
                        <option key={band} value={band}>
                          {lockPrefix}
                          {formatQuizDifficulty(band)}
                        </option>
                      );
                    })}
                  </Form.Select>
                ) : (
                  formatQuizDifficulty(soloPlayModeChoices(soloConfirmQuiz)[0] ?? soloConfirmQuiz.difficulty)
                )}
              </div>
              <div className="mb-0">
                <strong>{t('pvp.inviteJoinCost', 'Cost')}:</strong>{' '}
                <span className="d-inline-flex align-items-center gap-1 flex-wrap">
                  <InlineHalfHeartIcon className="pvp-invite-join-cost-heart" />
                  <span>× 1</span>
                </span>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between flex-wrap gap-2">
          <Button variant="outline-secondary" onClick={() => setSoloConfirmQuiz(null)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          {!isUserLoggedIn ? (
            <Button
              variant="primary"
              onClick={() => setShowLoginModal(true)}
              disabled={!soloConfirmQuiz}
            >
              {t('quiz.soloJoinLogin', 'Log in to play')}
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={!soloConfirmQuiz || startingQuizId !== null}
              onClick={() => {
                if (!soloConfirmQuiz) return;
                if (
                  user &&
                  !isUnlimitedUserType(user.userType) &&
                  (user.lives ?? 0) < 1
                ) {
                  setSoloConfirmQuiz(null);
                  setLivesGateMessage('');
                  setLivesGateOpen(true);
                  return;
                }
                void runSoloQuizAfterConfirm(soloConfirmQuiz);
              }}
            >
              {startingQuizId === soloConfirmQuiz?.id ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t('quiz.loadingQuiz', 'Loading quiz...')}
                </>
              ) : (
                t('quiz.play', 'Play')
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <PvpCharacterUnlockRosterModal
        show={unlockRosterModal !== null}
        quizId={unlockRosterModal?.quizId ?? null}
        quizCaption={unlockRosterModal?.caption ?? ''}
        imageAssetsBasePathFallback={unlockRosterModal?.imageAssetsBasePathFallback ?? null}
        onHide={() => setUnlockRosterModal(null)}
      />

      {/* Login Modal */}
      <Modal 
        show={showLoginModal} 
        onHide={() => setShowLoginModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
                     <Modal.Title>{t('auth.loginRequired', 'Login Required')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LoginPage onLoginSuccess={handleLoginSuccess} />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default QuizList; 